import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import App from './App'
import './styles/theme.css'
import './styles/globals.css'
import { initPerformanceMonitoring } from './utils/performance'

// One-time cleanup of legacy localStorage cache keys
// This removes stale apex_cache_* entries from users who had the old caching system
if (typeof window !== 'undefined') {
  try {
    const cacheKeys = Object.keys(localStorage).filter(k => k.startsWith('apex_cache_'))
    if (cacheKeys.length > 0) {
      cacheKeys.forEach(k => localStorage.removeItem(k))
      console.log(`[APEX] Cleaned up ${cacheKeys.length} legacy cache entries`)
    }
  } catch {
    // localStorage may not be available in some contexts
  }
}

// Initialize web vitals monitoring
initPerformanceMonitoring()

// Initialize accessibility testing in development
if (import.meta.env.DEV) {
  import('@axe-core/react').then(axe => {
    import('react-dom').then(ReactDOM => {
      import('react').then(React => {
        axe.default(React, ReactDOM, 1000)
      })
    })
  }).catch(() => {})
}

// Lazy load analytics libraries after initial render for better FCP/LCP
const initAnalytics = () => {
  // Initialize Sentry error monitoring - lazy loaded
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN
  if (sentryDsn) {
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.MODE,
      })
    })
  }

  // Initialize PostHog analytics - lazy loaded
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY
  if (posthogKey) {
    import('posthog-js').then((posthogModule) => {
      const posthog = posthogModule.default
      posthog.init(posthogKey, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
        },
      })
    })
  }
}

// Load analytics after page is interactive
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initAnalytics)
  } else {
    setTimeout(initAnalytics, 2000)
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,           // 30 seconds default
      gcTime: 5 * 60 * 1000,          // 5 min garbage collection
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnWindowFocus: true,     // Refetch stale data on window focus (default RQ behavior)
      refetchOnReconnect: true,
    },
  },
})

// Export queryClient for use in 401 handling and offline detection
export { queryClient }

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope)

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available, show update notification
                if (window.confirm('New version available! Reload to update?')) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' })
                  window.location.reload()
                }
              }
            })
          }
        })
      })
      .catch((error) => {
        console.log('SW registration failed:', error)
      })

    // Handle controller change (when new SW takes over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload()
    })
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>,
)
